/**
 * Wireline & Slickline batch 6 — the AI drafts (fieldiq-api#1362, WL-6). Typed
 * from routes/wireline-slickline-ai.routes.ts and lib/wl-ai-digests.ts as
 * merged at bd7ac580; the manifest mirror carries the same routes.
 *
 * camelCase reads, `{ success, data }`. Every run is a HITL draft (agent_runs,
 * outcome 'pending'): `data` is the API's deterministic result, `draft` the
 * model's text (null when it was skipped or rejected — `aiError` says why).
 * Nothing is published until a person approves it.
 */

export const WL_AI_AGENTS = ['wl_sheet_summary', 'wl_ticket_preflight', 'wl_service_report_draft', 'wl_compliance_digest'] as const;
export type WlAiAgent = (typeof WL_AI_AGENTS)[number];

export interface WlAiDraft {
  narrative: string;
  observations: string[];
}

// ─── Day-sheet summary ───────────────────────────────────────────────────────

export interface WlSheetSummaryData {
  sheet: {
    id: string;
    workDate: string;
    status: string;
    startTime: string | null;
    endTime: string | null;
    jobId: string;
    jobNumber: string;
    unitNumber: string | null;
    wellName: string | null;
    customerName: string | null;
  };
  depthUnit: string;
  lengthUnit: string;
  hours: { sheet: number | null; insideRuns: number | null; standby: number | null; travel: number | null; crew: number | null; crewTravel: number | null };
  timeline: { gaps: Array<{ from: string; to: string; minutes: number }>; complete: boolean | null };
  runsByKind: Array<{ kind: string; label: string; runs: number; footage: number | null }>;
  runs: Array<{ runNumber: number; kind: string; rigUp: string | null; rigDown: string | null; maxDepth: string | null; result: string | null; footage: string | null }>;
  /** In `lengthUnit`. */
  footage: number | null;
  /** In `depthUnit`. */
  deepest: number | null;
  shots: { guns: number; loaded: number; fired: number; derivedMisfires: number };
  misfireWarnings: Array<{ runNumber: number; gunPosition: number; misfireCount: number; derivedMisfires: number }>;
  plugs: Array<{ runNumber: number; action: string; kind: string; depth: string | null }>;
  surveyStops: number | null;
  logIntervals: number | null;
  sources: Array<{ runNumber: number; isotope: string; serialNumber: string | null }>;
  rigUpTestMissing: boolean | null;
  crew: Array<{ name: string; position: string | null; hours: number | null }>;
  pressureTests: Array<{ testedOn: string | null; kind: string; result: string }>;
  drills: Array<{ drilledOn: string | null; kind: string; secondsToSecure: number | null }>;
  remarks: string | null;
}

// ─── Preflight (one ticket, or one job's open tickets and approved sheets) ──

export interface WlRunKindGap {
  runKind: string;
  label: string;
  days: string[];
  runs: number;
}

export interface WlPreflightData {
  scope: 'ticket' | 'job';
  job: { id: string; jobNumber: string };
  ticket: { id: string; ticketNumber: string; status: string; scope: string; workDate: string | null } | null;
  currency: string | null;
  ticketsChecked: number | null;
  sheetsChecked: number | null;
  unpricedLines: Array<{ ticketId: string; ticketNumber: string; lineId: string; description: string | null; reason: string | null }>;
  sheetsWithoutTicket: Array<{ sheetId: string; workDate: string; unitNumber: string | null }>;
  runKindsWithoutRate: WlRunKindGap[];
  flagCount: number | null;
  ready: boolean | null;
}

// ─── Service report draft ────────────────────────────────────────────────────

export interface WlReportDraftData {
  report: { id: string; status: string };
  job: { id: string; jobNumber: string };
  content: unknown;
}

// ─── Compliance digest ───────────────────────────────────────────────────────

export interface WlComplianceItemBase {
  /**
   * lib/wl-ai-digests.ts STATE_ORDER: expired, recert_required, overdue,
   * failed, lost_in_hole, over_level, out_too_long, missing, never_tested,
   * no_stack, would_fail_on_job, expiry_not_recorded, due_soon.
   */
  state: string;
  dueOn: string | null;
}

export interface WlComplianceDigestData {
  today: string;
  pressureControl: Array<WlComplianceItemBase & { equipmentId: string; label: string; component: string | null; jobNumbers: string[] }>;
  stackGuard: Array<WlComplianceItemBase & { equipmentId: string; label: string; jobNumber: string | null; unitNumber: string | null; plannedEndOn: string | null; code: string | null; error: string | null }>;
  unitsWithoutStack: Array<WlComplianceItemBase & { jobId: string; jobNumber: string; unitNumber: string | null }>;
  gauges: Array<WlComplianceItemBase & { equipmentId: string; label: string }>;
  lines: Array<WlComplianceItemBase & { lineId: string; lineNumber: string; check: 'pull_test' | 'inspection' | string }>;
  leakTests: Array<WlComplianceItemBase & { sourceId: string; isotope: string; serialNumber: string | null }>;
  sourcesOut: Array<WlComplianceItemBase & { sourceId: string; isotope: string; serialNumber: string | null; hoursOut: number | null; alertHours: number | null }>;
  sourcesLost: Array<WlComplianceItemBase & { sourceId: string; isotope: string; serialNumber: string | null }>;
  doses: Array<WlComplianceItemBase & { userId: string; name: string; periodStart: string | null; periodEnd: string | null; deepDoseMsv: number | null; levelMsv: number | null }>;
  crewCerts: Array<WlComplianceItemBase & { userId: string; name: string; cert: string; jobNumbers: string[] }>;
  total: number | null;
}

// ─── A run, as POST returns it and as the register stores it ─────────────────

/** POST /ai/* — 201. */
export interface WlAiRunResult<T = unknown> {
  agentRunId: string;
  agent: WlAiAgent | string;
  status: 'draft' | string;
  data: T | null;
  draft: WlAiDraft | null;
  aiError: string | null;
}

/** GET /ai/digests (≤ 20, newest first) and GET /ai/digests/:id — shapeWlRun. */
export interface WlAiStoredRun<T = unknown> {
  agentRunId: string;
  agent: WlAiAgent | string;
  status: string;
  /** agent_runs.outcome: pending | actioned | dismissed | superseded | no_action. */
  outcome: string | null;
  trigger: string | null;
  triggerData: Record<string, unknown> | null;
  data: T | null;
  draft: WlAiDraft | null;
  approved: WlAiDraft | null;
  approvedEdited: boolean | null;
  aiError: string | null;
  createdAt: string | null;
  reviewedAt: string | null;
}

/** POST /ai/digests/:id/approve — omit both to approve the draft as written. */
export interface WlAiApproveBody {
  narrative?: string;
  observations?: string[];
}

// ─── Required crew certificates (WL-6, 0627) ─────────────────────────────────

/** GET / PUT /wireline-slickline/settings/required-certificates. */
export interface WlRequiredCertificates {
  /** The tenant's list, in catalog order; the default list when it has stated none. */
  certificates: Array<{ key: string; label: string }>;
  /** True when the tenant has stated no list and the default applies. */
  isDefault: boolean;
  defaultKeys: string[];
  /** Every requirable certificate, in display order. */
  catalog: Array<{ key: string; label: string }>;
}
